import { useMemo, useCallback } from "react";
import { useLLMSelectionStore } from "../store/llm-selection-store";
import { useLLMModels } from "@/api/hooks";
import type { LLMModel } from "@/api/models/api-types";
import { useDebounce } from "@/hooks/use-debounce";
import { useToast } from "@/features/toast";
import mixpanel from "mixpanel-browser";

export const useLLMDialog = () => {
  const {
    isOpen,
    selectedProvider,
    searchQuery,
    visibleItems,
    openModal,
    closeModal,
    selectProvider,
    updateSearchQuery,
    loadMoreItems,
    selectModel: selectModelAction,
  } = useLLMSelectionStore();

  const { info } = useToast();

  // 🚀 Debounce search query - 500ms bekle (API çağrılarını optimize et)
  const debouncedSearchQuery = useDebounce(searchQuery || "", 500);

  // Backend'den LLM models getir - Sadece dialog açıkken, API search ile
  const {
    data: modelsData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLLMModels({
    enabled: isOpen, // 🎯 Lazy loading: Sadece dialog açıkken fetch et
    provider_type: "llm", // AI model selection ile aynı provider type
    searchTerm: debouncedSearchQuery, // 🚀 Debounced API search - local search yerine
  });

  // Tüm modeller (artık doğrudan API'den geliyor)
  const allModels = useMemo(() => {
    const models = modelsData?.pages
      ?.flatMap((page) => page.data.result)
      ?.filter((model) => !model.isDeleted);
    return models || [];
  }, [modelsData]);

  // Unique provider'ları çıkar
  const providers = useMemo(() => {
    const uniqueProviders = new Map<
      string,
      { _id: string; name: string; isDeleted: boolean }
    >();
    let hasUnknown = false;
    allModels.forEach((model) => {
      if (model.provider && model.provider._id) {
        const pid = model.provider._id as string;
        if (!uniqueProviders.has(pid)) {
          uniqueProviders.set(pid, {
            _id: pid,
            name: model.provider.name,
            isDeleted: false,
          });
        }
      } else {
        hasUnknown = true;
      }
    });
    const list = Array.from(uniqueProviders.values());
    if (hasUnknown) {
      list.push({ _id: "unknown", name: "Unknown", isDeleted: false });
    }
    return list;
  }, [allModels]);

  // Provider kategorileri
  const providerCategories = useMemo(() => {
    const categories = [{ name: "Tümü", slug: "all" }];
    providers.forEach((provider) => {
      if (!provider.isDeleted) {
        categories.push({
          name: provider.name,
          slug: provider._id,
        });
      }
    });
    return categories;
  }, [providers]);

  // Filtrelenmiş modeller - Sadece provider filtrelemesi client-side (API search kullanıyoruz)
  const filteredModels = useMemo(() => {
    // 🚀 Search artık API üzerinden yapılıyor, sadece provider filtrelemesi client-side
    return allModels.filter((model) => {
      const matchesProvider =
        selectedProvider === "all" ||
        (selectedProvider === "unknown" && !model.provider) ||
        (!!model.provider && model.provider._id === selectedProvider);

      return matchesProvider;
    });
  }, [allModels, selectedProvider]);

  // Görünür modeller
  const visibleModels = useMemo(() => {
    return filteredModels.slice(0, visibleItems);
  }, [filteredModels, visibleItems]);

  // Scroll handler
  const handleScroll = useCallback(
    async (element: HTMLDivElement) => {
      if (!element) return;

      const { scrollTop, scrollHeight, clientHeight } = element;
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        // Client-side görünürlük artışı
        loadMoreItems();
        // Server-side sonraki sayfayı getir
        if (hasNextPage && !isFetchingNextPage) {
          await fetchNextPage();
        }
      }
    },
    [loadMoreItems, fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  // Model selection with callback support
  const selectModel = useCallback(
    (model: LLMModel, onSelect?: (model: LLMModel) => void) => {
      selectModelAction(model);
      info(
        `Dil modeli değiştirildi: <span style='font-weight:600'>${
          model.title || model.name
        }</span>`,
        { title: "Bilgi" },
      );
      onSelect?.(model);
      mixpanel.track("model_selected", {
        model_id: model?._id,
        model_name: model?.name,
        type: "llm selection dialog",
      });
      window.netmera.push(function (api) {
        api.sendEvent({
          code: "lvpxz",
          ea: model?._id,
          eb: model?.name,
          ef: "llm selection dialog",
        });
      });
    },
    [selectModelAction, info],
  );

  return {
    // State
    isOpen,
    selectedProvider,
    searchQuery,
    providerCategories,
    visibleModels,
    filteredModels,
    allModels,
    providers,
    isLoading,
    error,

    // Actions
    openModal,
    closeModal,
    selectProvider,
    updateSearchQuery,
    selectModel,
    handleScroll,

    // Infinite state
    hasNextPage,
    isLoadingMore: isFetchingNextPage,

    // Computed
    hasMoreItems: visibleItems < filteredModels.length,
    totalModels: allModels.length,
    filteredCount: filteredModels.length,
  };
};
